import React, { useMemo, useState } from 'react';
import ConditionRow from './AutomationBuilder/ConditionRow';
import ActionRow from './AutomationBuilder/ActionRow';
import AutomationTestPanel from './AutomationTestPanel';

const TRIGGERS = [
    'lead.created',
    'lead.updated',
    'deal.created',
    'deal.stage_changed',
    'activity.completed',
    'activity.created',
];

const AutomationEditor = ({ draft, setDraft, isNew, onSave, onCancel, onDuplicate, disabled, templates }) => {
    const [activeTab, setActiveTab] = useState('builder');

    const hasDraft = useMemo(() => Boolean(draft), [draft]);
    if (!hasDraft) {
        return (
            <div className="crm-card crm-card-hover">
                <div className="crm-card-body">
                    <div className="crm-empty-state">
                        <p>Select an automation to edit.</p>
                    </div>
                </div>
            </div>
        );
    }

    const updateCondition = (index, next) => {
        const conditions = [...(draft.conditions || [])];
        conditions[index] = next;
        setDraft({ ...draft, conditions });
    };

    const updateAction = (index, next) => {
        const actions = [...(draft.actions || [])];
        actions[index] = next;
        setDraft({ ...draft, actions });
    };

    return (
        <div className="crm-card crm-card-hover">
            <div className="crm-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 className="crm-card-title">{isNew ? 'New Automation' : 'Edit Automation'}</h2>
                <div style={{ display: 'flex', gap: '8px' }}>
                    {!isNew && (
                        <button className="crm-btn crm-btn-secondary crm-btn-sm" onClick={onDuplicate} disabled={disabled}>
                            Duplicate
                        </button>
                    )}
                    <button className="crm-btn crm-btn-secondary crm-btn-sm" onClick={onCancel} disabled={disabled}>
                        Reset
                    </button>
                    <button className="crm-btn crm-btn-primary crm-btn-sm" onClick={onSave} disabled={disabled}>
                        Save
                    </button>
                </div>
            </div>
            <div className="crm-tab-bar" style={{ margin: '0 16px' }}>
                {['builder', 'test'].map((tab) => (
                    <button
                        key={tab}
                        className={`crm-tab ${activeTab === tab ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab)}
                        type="button"
                    >
                        {tab === 'builder' ? 'Builder' : 'Test'}
                    </button>
                ))}
            </div>
            <div className="crm-card-body">
                {activeTab === 'builder' && (
                    <>
                        <div className="crm-form-grid">
                            <div className="crm-form-group">
                                <label className="crm-label">Name</label>
                                <input
                                    className="crm-input"
                                    value={draft.name || ''}
                                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                                />
                            </div>
                            <div className="crm-form-group">
                                <label className="crm-label">Enabled</label>
                                <select
                                    className="crm-select"
                                    value={draft.enabled ? 'true' : 'false'}
                                    onChange={(e) => setDraft({ ...draft, enabled: e.target.value === 'true' })}
                                >
                                    <option value="true">Enabled</option>
                                    <option value="false">Disabled</option>
                                </select>
                            </div>
                            <div className="crm-form-group">
                                <label className="crm-label">Trigger</label>
                                <select
                                    className="crm-select"
                                    value={draft.trigger || 'lead.created'}
                                    onChange={(e) => setDraft({ ...draft, trigger: e.target.value })}
                                >
                                    {TRIGGERS.map((trigger) => (
                                        <option key={trigger} value={trigger}>{trigger}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="crm-form-group">
                                <label className="crm-label">Delay (mins)</label>
                                <input
                                    className="crm-input"
                                    type="number"
                                    value={draft.delayMins || ''}
                                    onChange={(e) => setDraft({ ...draft, delayMins: e.target.value })}
                                />
                            </div>
                            <div className="crm-form-group">
                                <label className="crm-label">Rate limit per entity (mins)</label>
                                <input
                                    className="crm-input"
                                    type="number"
                                    value={draft.rateLimit?.perEntityMins || ''}
                                    onChange={(e) => setDraft({
                                        ...draft,
                                        rateLimit: { perEntityMins: e.target.value },
                                    })}
                                />
                            </div>
                        </div>

                        <div className="crm-section-divider" />

                        <div style={{ display: 'grid', gap: '12px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h3 className="crm-card-title">Conditions</h3>
                                <button
                                    className="crm-btn crm-btn-secondary crm-btn-sm"
                                    onClick={() => setDraft({
                                        ...draft,
                                        conditions: [...(draft.conditions || []), { field: '', op: 'eq', value: '' }],
                                    })}
                                >
                                    + Add Condition
                                </button>
                            </div>
                            {(draft.conditions || []).length === 0 && (
                                <p className="crm-muted">No conditions. This automation always runs on trigger.</p>
                            )}
                            {(draft.conditions || []).map((condition, index) => (
                                <ConditionRow
                                    key={`${condition.field}-${index}`}
                                    condition={condition}
                                    onChange={(next) => updateCondition(index, next)}
                                    onRemove={() => {
                                        const next = [...draft.conditions];
                                        next.splice(index, 1);
                                        setDraft({ ...draft, conditions: next });
                                    }}
                                />
                            ))}
                        </div>

                        <div className="crm-section-divider" />

                        <div style={{ display: 'grid', gap: '12px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h3 className="crm-card-title">Actions</h3>
                                <button
                                    className="crm-btn crm-btn-secondary crm-btn-sm"
                                    onClick={() => setDraft({
                                        ...draft,
                                        actions: [...(draft.actions || []), { type: 'lead.assign_round_robin' }],
                                    })}
                                >
                                    + Add Action
                                </button>
                            </div>
                            {(draft.actions || []).length === 0 && (
                                <p className="crm-muted">Add at least one action.</p>
                            )}
                            {(draft.actions || []).map((action, index) => (
                                <ActionRow
                                    key={`${action.type}-${index}`}
                                    action={action}
                                    templates={templates}
                                    onChange={(next) => updateAction(index, next)}
                                    onRemove={() => {
                                        const next = [...draft.actions];
                                        next.splice(index, 1);
                                        setDraft({ ...draft, actions: next });
                                    }}
                                />
                            ))}
                        </div>
                    </>
                )}

                {activeTab === 'test' && (
                    <AutomationTestPanel trigger={draft.trigger} />
                )}
            </div>
        </div>
    );
};

export default AutomationEditor;
